(() => {
  'use strict';

  // Versioned asset name prevents stale optimizer/CDN copies in Shopify previews.

  const QUICK_SHOP_FORM = 'form[id*="main-qs"]';
  const PRODUCT_FORM = 'form[data-type="add-to-cart-form"]';
  const MAIN_PRODUCT_FORM = `${PRODUCT_FORM}:not([id*="main-qs"])`;
  const PROTECTED_FORM = `${QUICK_SHOP_FORM}, ${PRODUCT_FORM}`;
  const STICKY_ATC = '[data-sticky-addtocart]';
  const OPTION_GROUP = '[data-swatch-option]';
  const OPTION_ITEM = '[data-swatch-item]';
  const QUICK_SHOP_TRIGGER = '[data-action-quickshop]';
  const AVAILABLE_OPTION_ITEM =
    `${OPTION_ITEM}:not([disabled]):not(.is--soldout):not(.is--unavailable)`;
  const ERROR_CLASS = 'evano-variant-choice-error';
  const UNCONFIRMED_CLASS = 'is--evano-size-unconfirmed';
  const SIZE_PROMPT = 'Selecciona una talla';
  const confirmedSizesByForm = new WeakMap();
  const explicitVariantInitializedForms = new WeakSet();
  const explicitSizeUrlClearedForms = new WeakSet();
  const observedForms = new WeakSet();
  const quickShopLoads = new WeakMap();
  const quickShopTouchStarts = new WeakMap();
  const pendingQuickShopTouchActivations = new WeakMap();
  const recoveredQuickShopActivations = new WeakMap();
  const QUICK_SHOP_HISTORY_KEY = 'evanoQuickShopOpen';
  let pendingQuickShopVariantId = '';
  let quickShopHistoryActive = false;
  let quickShopHistoryFrame = 0;
  let quickShopHistoryTimer = 0;
  let pendingAddToCart = null;

  const pushQuickShopCroEvent = (event, form, details = {}) => {
    const quickShop = form?.closest('.t4s-product-quick-shop');
    const payload = {
      event,
      evano_event_version: '1.0',
      page_path: window.location.pathname,
      source: 'quick_shop',
      product_title: quickShop?.querySelector('.t4s-product__title')?.textContent.trim() || '',
      ...details
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('evano:cro-event', {
      detail: payload
    }));
  };

  const SIZE_OPTION_PATTERN = /(^|\s)(talla|size)(\s|$)/i;
  const initialVariantId = (() => {
    const navigationUrl = window.performance
      ?.getEntriesByType?.('navigation')
      ?.[0]?.name;

    if (!navigationUrl) return null;

    return new URL(navigationUrl).searchParams.get('variant');
  })();

  const quickShopIsOpen = () =>
    document.documentElement.classList.contains('t4s-modal-opened') &&
    Boolean(document.querySelector('form[id*="main-qs"]'));

  const quickShopIsOpening = () =>
    quickShopIsOpen() ||
    document.documentElement.classList.contains('t4s-opening-qs');

  const isRenderedControl = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && rect.width > 0
      && rect.height > 0;
  };

  const historyStateObject = () => {
    const state = window.history.state;
    return state && typeof state === 'object' ? { ...state } : {};
  };

  const closeQuickShopFromHistory = () => {
    const closeButton = Array.from(
      document.querySelectorAll('button.t4s-modal-close')
    ).find(isRenderedControl);

    if (closeButton) {
      closeButton.click();
      return;
    }

    const visibleOverlay = Array.from(
      document.querySelectorAll('.t4s-close-overlay')
    ).find(isRenderedControl);
    visibleOverlay?.click();
  };

  const syncQuickShopHistory = () => {
    const replenishmentClose = document.querySelector(
      '#t4s-pr-popup__notify-stock .mfp-close'
    );
    if (replenishmentClose) {
      replenishmentClose.setAttribute('aria-label', 'Cerrar aviso de disponibilidad');
      replenishmentClose.setAttribute('title', 'Cerrar');
    }

    const isOpen = quickShopIsOpen();

    if (isOpen && !quickShopHistoryActive) {
      const state = historyStateObject();
      state[QUICK_SHOP_HISTORY_KEY] = true;
      window.history.pushState(state, '', window.location.href);
      quickShopHistoryActive = true;
      return;
    }

    if (!quickShopIsOpening() && quickShopHistoryActive) {
      // The close button or overlay dismissed the sheet. Consume only the
      // synthetic same-page entry so the customer's real journey is intact.
      quickShopHistoryActive = false;
      window.history.back();
    }
  };

  const scheduleQuickShopHistorySync = () => {
    window.cancelAnimationFrame(quickShopHistoryFrame);
    window.clearTimeout(quickShopHistoryTimer);
    quickShopHistoryFrame = window.requestAnimationFrame(syncQuickShopHistory);
    quickShopHistoryTimer = window.setTimeout(syncQuickShopHistory, 140);
  };

  window.addEventListener('popstate', (event) => {
    if (quickShopHistoryActive) {
      // Browser/Android/iOS Back consumes the synthetic modal entry and closes
      // the sheet instead of sending the shopper to a previous collection.
      quickShopHistoryActive = false;
      if (quickShopIsOpening()) closeQuickShopFromHistory();
      return;
    }

    if (event.state?.[QUICK_SHOP_HISTORY_KEY] && !quickShopIsOpening()) {
      const state = historyStateObject();
      delete state[QUICK_SHOP_HISTORY_KEY];
      window.history.replaceState(state, '', window.location.href);
    }
  });

  new MutationObserver(scheduleQuickShopHistorySync).observe(
    document.documentElement,
    { attributes: true, attributeFilter: ['class'] }
  );

  const restoreQuickShopTrigger = (trigger) => {
    const state = quickShopLoads.get(trigger);
    if (!state) return;

    if (quickShopIsOpen()) {
      pushDiagnostic('evano_quick_shop_ready', {
        product_id: state.productId,
        variant_id: state.variantId,
        duration_ms: Math.round(window.performance.now() - state.startedAt)
      });
    }

    window.clearTimeout(state.fallbackTimer);
    window.cancelAnimationFrame(state.checkFrame);
    state.observer.disconnect();
    trigger.classList.remove('evano-quickshop-loading');
    trigger.removeAttribute('aria-busy');
    if (state.label) state.label.textContent = state.labelText;
    if (state.ariaLabel == null) trigger.removeAttribute('aria-label');
    else trigger.setAttribute('aria-label', state.ariaLabel);
    quickShopLoads.delete(trigger);
  };

  const beginQuickShopLoad = (event, trigger) => {
    if (quickShopLoads.has(trigger)) {
      // Never swallow a repeated tap. Mobile Safari can spend the first tap
      // revealing a hover-driven control instead of reaching Gecko's handler.
      // The next activation must remain available to the native quick shop.
      return;
    }

    const label = trigger.querySelector('.t4s-text-pr');
    const state = {
      ariaLabel: trigger.getAttribute('aria-label'),
      label,
      labelText: label?.textContent || '',
      productId: trigger.dataset.id || null,
      variantId: trigger.dataset.variantId || null,
      startedAt: window.performance.now(),
      observer: null,
      fallbackTimer: null,
      checkFrame: 0
    };
    const checkWhenOpen = () => {
      state.checkFrame = 0;
      if (quickShopIsOpen()) restoreQuickShopTrigger(trigger);
    };
    const completeWhenOpen = () => {
      if (state.checkFrame) return;
      state.checkFrame = window.requestAnimationFrame(checkWhenOpen);
    };

    state.observer = new MutationObserver(completeWhenOpen);
    state.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    state.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    state.fallbackTimer = window.setTimeout(() => {
      if (quickShopIsOpen()) {
        restoreQuickShopTrigger(trigger);
        return;
      }

      const fallbackUrl = trigger.href;
      pushDiagnostic('evano_purchase_path_error', {
        reason: 'quick_shop_timeout',
        purchase_context: 'collection',
        product_id: state.productId,
        variant_id: state.variantId
      });
      restoreQuickShopTrigger(trigger);
      if (fallbackUrl) window.location.assign(fallbackUrl);
    }, 6500);

    quickShopLoads.set(trigger, state);
    trigger.classList.add('evano-quickshop-loading');
    trigger.setAttribute('aria-busy', 'true');
    trigger.setAttribute('aria-label', 'Cargando selector de tallas');
    if (label) label.textContent = 'Cargando tallas…';
    window.requestAnimationFrame(completeWhenOpen);
  };

  const isSizeGroup = (group) => {
    if (!group) return false;

    const labelId = group.getAttribute('aria-labelledby');
    const label = labelId ? document.getElementById(labelId)?.textContent : '';
    const optionName = `${group.className} ${label || ''}`
      .replace(/[_-]+/g, ' ')
      .trim();

    return (
      SIZE_OPTION_PATTERN.test(optionName) &&
      group.querySelectorAll(OPTION_ITEM).length > 1
    );
  };

  const sizeGroups = (form) =>
    Array.from(form.querySelectorAll(OPTION_GROUP)).filter(isSizeGroup);

  const purchaseContext = (form) =>
    form?.matches(QUICK_SHOP_FORM) ? 'quick_shop' : 'product_page';

  const selectedVariantId = (form) =>
    form?.querySelector('input[name="id"], select[name="id"]')?.value || null;

  const pushDiagnostic = (event, details = {}) => {
    const payload = {
      event,
      evano_event_version: '1.0',
      event_category: 'purchase_path_reliability',
      ...details
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('evano:cro-event', {
      detail: payload
    }));
  };

  const beginAddToCartTiming = (form) => {
    const now = window.performance.now();
    if (
      pendingAddToCart
      && pendingAddToCart.form === form
      && now - pendingAddToCart.startedAt < 1500
    ) return;

    pendingAddToCart = {
      form,
      startedAt: now,
      context: purchaseContext(form),
      variantId: selectedVariantId(form)
    };

    pushDiagnostic('evano_add_to_cart_started', {
      purchase_context: pendingAddToCart.context,
      variant_id: pendingAddToCart.variantId
    });
  };

  const completeAddToCartTiming = () => {
    if (!pendingAddToCart) return;

    pushDiagnostic('evano_add_to_cart_completed', {
      purchase_context: pendingAddToCart.context,
      variant_id: pendingAddToCart.variantId,
      duration_ms: Math.round(
        window.performance.now() - pendingAddToCart.startedAt
      )
    });
    pendingAddToCart = null;
  };

  const syncSizeOptionAccessibility = (form) => {
    sizeGroups(form).forEach((group) => {
      group.querySelectorAll(OPTION_ITEM).forEach((option) => {
        const value = option.dataset.value || option.textContent.trim();
        const selected = option.classList.contains('is--selected');
        const unavailable = option.disabled
          || option.classList.contains('is--soldout')
          || option.classList.contains('is--unavailable');
        const label = unavailable ? `Talla ${value} — Agotada` : `Talla ${value}`;
        const ariaDisabled = String(Boolean(option.disabled));

        option.setAttribute('role', 'radio');
        option.setAttribute('aria-checked', String(selected));
        if (option.getAttribute('aria-label') !== label) {
          option.setAttribute('aria-label', label);
        }
        if (option.getAttribute('aria-disabled') !== ariaDisabled) {
          option.setAttribute('aria-disabled', ariaDisabled);
        }
      });
    });
  };

  const notifyButton = (form) =>
    form.querySelector('[data-evano-notify-stock]') ||
    form.querySelector('[data-notify-stock-btn]');

  const requiresExplicitSize = (form) =>
    form?.dataset.evanoRequireExplicitSize === 'true' ||
    (form?.matches(QUICK_SHOP_FORM) && sizeGroups(form).length > 0);

  const groupKey = (group) =>
    group.dataset.id || group.getAttribute('aria-labelledby') || group.className;

  const selectedSizeValue = (group) =>
    group.querySelector(`${OPTION_ITEM}.is--selected`)?.dataset.value || null;

  const confirmedSizeValue = (form, group) =>
    confirmedSizesByForm.get(form)?.get(groupKey(group)) || null;

  const rememberConfirmedSize = (form, group, value) => {
    if (!value) return;

    let confirmedSizes = confirmedSizesByForm.get(form);
    if (!confirmedSizes) {
      confirmedSizes = new Map();
      confirmedSizesByForm.set(form, confirmedSizes);
    }

    confirmedSizes.set(groupKey(group), value);
    group.dataset.evanoConfirmedValue = value;
    group.classList.remove(UNCONFIRMED_CLASS);
  };

  const presentConfirmedSize = (group, value) => {
    group.querySelectorAll(OPTION_ITEM).forEach((option) => {
      const selected = option.dataset.value === value;
      option.classList.toggle('is--selected', selected);
      option.setAttribute('aria-pressed', String(selected));
      if (option.getAttribute('role') === 'radio') {
        option.setAttribute('aria-checked', String(selected));
      }
    });
    group.querySelectorAll('[data-current-value]').forEach((currentValue) => {
      currentValue.textContent = value;
    });
  };

  const productVariants = (form) => {
    const source = form.closest('.t4s-product-form__variants')
      ?.querySelector('.pr_variants_json');
    if (!source?.textContent) return [];
    try {
      const variants = JSON.parse(source.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error('EVANO could not read product variants.', error);
      return [];
    }
  };

  const updateQuickShopSizeAvailability = (
    form,
    changedGroup = null,
    changedValue = ''
  ) => {
    if (!form?.matches(QUICK_SHOP_FORM)) return new Map();

    const groups = [...form.querySelectorAll(OPTION_GROUP)];
    const sizeGroup = groups.find(isSizeGroup);
    const sizeIndex = groups.indexOf(sizeGroup);
    const variants = productVariants(form);
    const availability = new Map();
    if (!sizeGroup || sizeIndex < 0 || !variants.length) return availability;

    const selections = groups.map((group) => {
      if (group === changedGroup) return changedValue || null;
      return group.querySelector(`${OPTION_ITEM}.is--selected`)?.dataset.value || null;
    });

    sizeGroup.querySelectorAll(OPTION_ITEM).forEach((option) => {
      const size = option.dataset.value || option.textContent.trim();
      const available = variants.some((variant) =>
        variant.available
        && variant.options?.[sizeIndex] === size
        && groups.every((_, index) =>
          index === sizeIndex
          || !selections[index]
          || variant.options?.[index] === selections[index]
        )
      );

      availability.set(size, available);
      option.classList.toggle('is--soldout', !available);
      // These variants exist but have no sellable inventory. Keep them visible
      // as sold out; Gecko hides `is--unavailable` options entirely, which makes
      // the size rail look incomplete after a color change.
      option.classList.remove('is--unavailable');
      option.disabled = !available;
      option.setAttribute('aria-disabled', String(!available));
      option.setAttribute(
        'aria-label',
        available ? `Talla ${size}` : `Talla ${size} — Agotada`
      );
    });

    const confirmedSize = confirmedSizeValue(form, sizeGroup);
    if (confirmedSize && availability.get(confirmedSize) === false) {
      forgetConfirmedSize(form, sizeGroup);
      resetSizeGroup(form, sizeGroup);
    }

    return availability;
  };

  const variantImageSource = (variant) => {
    const source = variant?.featured_image?.src
      || variant?.featured_media?.preview_image?.src
      || '';
    return typeof source === 'string' ? source : '';
  };

  const resizedImageSource = (source, width) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (error) {
      return source;
    }
  };

  const syncQuickShopVariantImage = (form, variant) => {
    const quickShop = form.closest('.t4s-product-quick-shop');
    const image = quickShop?.querySelector('[data-main-img-change]');
    const source = variantImageSource(variant);
    if (!quickShop || !image || !source) return false;

    const displayWidth = window.innerWidth < 768 ? 720 : 960;
    const previewWidth = window.innerWidth < 768 ? 480 : 720;
    const displaySource = resizedImageSource(source, displayWidth);
    const previewSource = resizedImageSource(source, previewWidth);
    const widths = [320, 480, 640, 720, 800, 960]
      .filter((width) => width <= displayWidth);
    const srcset = widths
      .map((width) => `${resizedImageSource(source, width)} ${width}w`)
      .join(', ');

    const applyImageAttributes = () => {
      image.src = displaySource;
      image.srcset = srcset;
      image.setAttribute('data-src', displaySource);
      image.setAttribute('data-srcset', srcset);
      image.setAttribute('data-widths', JSON.stringify(widths));
      image.sizes = window.innerWidth < 768 ? '42vw' : '36vw';
      image.loading = 'eager';
      image.fetchPriority = 'high';
    };

    const currentSrcset = image.getAttribute('srcset') || '';
    const currentDataSrcset = image.getAttribute('data-srcset') || '';
    const imageIsHealthy = image.getAttribute('src') === displaySource
      && image.naturalWidth > 1
      && currentSrcset.includes(`${displayWidth}w`)
      && currentDataSrcset.includes(`${displayWidth}w`);

    if (image.dataset.evanoRequestedVariantImage === displaySource) {
      if (imageIsHealthy) return true;

      // Gecko can rewrite the responsive source to a one-pixel candidate after
      // its native variant change. Restore the already loaded image in place.
      applyImageAttributes();
      quickShop.classList.remove('is--evano-image-pending');
      quickShop.classList.add('is--evano-image-ready');
      return true;
    }

    image.dataset.evanoRequestedVariantImage = displaySource;
    quickShop.classList.add('is--evano-image-pending');

    const loader = quickShop.querySelector('.t4s-product-qs__img .lazyloadt4s-loader');
    if (loader) {
      loader.style.backgroundImage = `url("${previewSource}")`;
      loader.style.backgroundPosition = 'center';
      loader.style.backgroundRepeat = 'no-repeat';
      loader.style.backgroundSize = 'contain';
    }

    const preload = new Image();
    preload.decoding = 'async';
    preload.fetchPriority = 'high';
    preload.onload = () => {
      if (image.dataset.evanoRequestedVariantImage !== displaySource) return;

      applyImageAttributes();

      const reveal = () => {
        if (image.dataset.evanoRequestedVariantImage !== displaySource) return;
        quickShop.classList.remove('is--evano-image-pending');
        quickShop.classList.add('is--evano-image-ready');
      };
      if (typeof image.decode === 'function') image.decode().catch(() => {}).finally(reveal);
      else window.requestAnimationFrame(reveal);
    };
    preload.onerror = () => {
      if (image.dataset.evanoRequestedVariantImage === displaySource) {
        quickShop.classList.remove('is--evano-image-pending');
      }
    };
    preload.src = previewSource;
    return true;
  };

  const scheduleQuickShopVariantImageSync = (form, variant) => {
    const image = form.closest('.t4s-product-quick-shop')?.querySelector('[data-main-img-change]');
    const source = variantImageSource(variant);
    const expectedSource = resizedImageSource(source, window.innerWidth < 768 ? 720 : 960);
    if (!image || !expectedSource) return;

    [0, 120, 420, 900, 1500].forEach((delay) => {
      window.setTimeout(() => {
        const select = form.querySelector('select[name="id"]');
        const selectionStillMatches = !select?.value
          || String(select.value) === String(variant.id);
        if (image.dataset.evanoRequestedVariantImage === expectedSource
          && selectionStillMatches) {
          syncQuickShopVariantImage(form, variant);
        }
      }, delay);
    });
  };

  const hydrateExplicitQuickShopImageSwatches = (form) => {
    if (!form?.matches(QUICK_SHOP_FORM) || !requiresExplicitSize(form)) return;

    const variants = productVariants(form);
    if (!variants.length) return;

    [...form.querySelectorAll(OPTION_GROUP)].forEach((group, index) => {
      if (isSizeGroup(group)) return;

      let hasImageSwatch = false;
      group.querySelectorAll(OPTION_ITEM).forEach((option) => {
        const variant = variants.find((candidate) =>
          candidate.options?.[index] === option.dataset.value
          && variantImageSource(candidate)
        );
        const source = variantImageSource(variant);
        if (!source) return;

        option.classList.add('is--evano-image-swatch');
        option.dataset.evanoVariantImage = resizedImageSource(source, 960);
        option.style.setProperty(
          '--evano-swatch-image',
          `url("${resizedImageSource(source, 180)}")`
        );
        const optionValue = option.dataset.value || 'Variante';
        const accessibleValue = group.classList.contains('is--evano-finish')
          ? `Acabado: ${option.textContent.trim() || optionValue}`
          : optionValue;
        option.setAttribute('aria-label', accessibleValue);
        hasImageSwatch = true;
      });

      group.classList.toggle('is--evano-image-swatches-ready', hasImageSwatch);
    });
  };

  const syncExplicitQuickShopChoice = (form, changedGroup, changedValue) => {
    if (!form?.matches(QUICK_SHOP_FORM) || !requiresExplicitSize(form)) return false;

    updateQuickShopSizeAvailability(form, changedGroup, changedValue);
    const groups = [...form.querySelectorAll(OPTION_GROUP)];
    const changedIndex = groups.indexOf(changedGroup);
    if (changedIndex < 0) return false;

    const selections = groups.map((group) => {
      if (group === changedGroup) return changedValue;
      return group.querySelector(`${OPTION_ITEM}.is--selected`)?.dataset.value || null;
    });
    const candidates = productVariants(form).filter((candidate) =>
      candidate.options?.every((value, index) =>
        selections[index] == null || value === selections[index]
      )
    );
    const variant = candidates.find((candidate) =>
      candidate.available && variantImageSource(candidate)
    ) || candidates.find((candidate) => variantImageSource(candidate)) || candidates[0];
    if (!variant) return false;

    syncQuickShopVariantImage(form, variant);
    scheduleQuickShopVariantImageSync(form, variant);

    if (selections.some((value) => value == null)) return true;

    const select = form.querySelector('select[name="id"]');
    const sizeIndex = groups.findIndex(isSizeGroup);
    const size = sizeIndex >= 0 ? selections[sizeIndex] : '';
    if (!select || !size) return true;

    select.value = String(variant.id);
    select.dispatchEvent(new Event('change', { bubbles: true }));
    syncResolvedPurchaseSurfaces(form, size, variant);
    document.dispatchEvent(new CustomEvent('evano:explicit-variant-resolved', {
      detail: { form, variantId: String(variant.id) }
    }));
    return true;
  };

  const applyQuickShopVariantContext = (form) => {
    if (!form?.matches(QUICK_SHOP_FORM)) return false;

    const requestedVariantId = form.dataset.evanoSourceVariantId || pendingQuickShopVariantId;
    if (!requestedVariantId) return false;

    const variant = productVariants(form).find(
      (candidate) => String(candidate.id) === String(requestedVariantId)
    );
    if (!variant) return false;

    form.dataset.evanoSourceVariantId = String(variant.id);
    pendingQuickShopVariantId = '';

    const contextWasApplied = form.dataset.evanoVariantContextApplied === String(variant.id);
    let matchedContext = false;

    [...form.querySelectorAll(OPTION_GROUP)].forEach((group, index) => {
      if (isSizeGroup(group)) return;
      const value = variant.options?.[index];
      if (!value) return;

      const option = [...group.querySelectorAll(OPTION_ITEM)].find(
        (candidate) => candidate.dataset.value === value
      );
      if (!option) return;

      const selected = option.classList.contains('is--selected')
        || option.getAttribute('aria-checked') === 'true'
        || option.getAttribute('aria-pressed') === 'true';
      if (contextWasApplied && !selected) return;
      if (!selected) option.click();
      matchedContext = true;
    });

    if (matchedContext) {
      form.dataset.evanoVariantContextApplied = String(variant.id);
      syncQuickShopVariantImage(form, variant);
    }
    return true;
  };

  const scheduleQuickShopVariantContext = (form) => {
    [0, 120, 420, 900].forEach((delay) => {
      window.setTimeout(() => applyQuickShopVariantContext(form), delay);
    });
  };

  document.addEventListener('evano:quickshop-variant-requested', (event) => {
    const variantId = event.detail?.variantId;
    if (!variantId) return;

    pendingQuickShopVariantId = String(variantId);
    const openQuickShop = document.querySelector(QUICK_SHOP_FORM);
    if (openQuickShop) scheduleQuickShopVariantContext(openQuickShop);
  });

  const syncResolvedPurchaseSurfaces = (form, size, variant) => {
    const mainAdd = form.querySelector('[data-atc-form], .t4s-product-form__submit');
    const sticky = document.querySelector(STICKY_ATC);
    const stickyAdd = sticky?.querySelector('[data-action-atc]');
    const mainLabel = mainAdd?.querySelector('.t4s-btn-atc_text');
    const stickyLabel = stickyAdd?.querySelector('.t4s-btn-atc_text');
    const price = sticky?.querySelector('[data-sticky-price]')?.textContent.trim() || '';
    const context = sticky?.querySelector('.evano-sticky-purchase-context');
    const notify = notifyButton(form);
    const groups = [...form.querySelectorAll(OPTION_GROUP)];
    const sizeIndex = groups.findIndex(isSizeGroup);
    const variantContext = variant.options
      ?.filter((_, index) => index !== sizeIndex)
      .filter(Boolean)
      .join(' · ') || '';

    if (mainLabel) mainLabel.textContent = variant.available
      ? 'Añadir al carrito'
      : `Talla ${size} agotada`;
    if (mainAdd) {
      mainAdd.setAttribute('aria-label', variant.available
        ? `Añadir talla ${size} al carrito`
        : `Talla ${size}, agotada`);
      mainAdd.dataset.evanoPurchaseState = variant.available ? 'ready' : 'sold-out';
      mainAdd.disabled = !variant.available;
      mainAdd.classList.toggle('t4s-d-none', !variant.available && Boolean(notify));
      if (variant.available) mainAdd.removeAttribute('aria-disabled');
      else mainAdd.setAttribute('aria-disabled', 'true');
    }
    if (stickyLabel) stickyLabel.textContent = variant.available
      ? 'Añadir al carrito'
      : `Talla ${size} agotada`;
    if (stickyAdd) {
      stickyAdd.dataset.variantId = variant.available ? String(variant.id) : '';
      stickyAdd.setAttribute('aria-label', variant.available
        ? `Añadir talla ${size} al carrito`
        : `Talla ${size}, agotada`);
      stickyAdd.dataset.evanoPurchaseState = variant.available ? 'ready' : 'sold-out';
      stickyAdd.disabled = !variant.available;
      if (variant.available) stickyAdd.removeAttribute('aria-disabled');
      else stickyAdd.setAttribute('aria-disabled', 'true');
    }
    if (context) context.textContent = `Talla ${size}${price ? ` · ${price}` : ''}`;
    if (notify) {
      notify.dataset.variantId = String(variant.id);
      notify.dataset.storageid = `notify-stock${variant.id}`;
      const rootUrl = notify.dataset.rootUrl || '/';
      notify.dataset.mfpSrc = `${rootUrl}variants/${variant.id}/?section_id=back-in-stock`;
      notify.classList.toggle('t4s-d-none', variant.available);
      const notifyLabel = notify.querySelector('[data-evano-notify-label]');
      const notifyContext = notify.querySelector('[data-evano-notify-context]');
      if (notifyLabel) notifyLabel.textContent = 'Avísame cuando vuelva';
      if (notifyContext) notifyContext.textContent = [
        `Talla ${size}`,
        variantContext,
        'Te avisamos por correo'
      ].filter(Boolean).join(' · ');
      if (!notifyLabel && !notifyContext) notify.textContent = 'Avísame cuando vuelva';
      notify.setAttribute('aria-label', [
        `Avísame cuando vuelva la talla ${size}`,
        variantContext
      ].filter(Boolean).join(', '));
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  };

  const resolveExplicitVariant = (form, changedGroup, changedValue) => {
    if (!requiresExplicitSize(form)) return;

    const selections = [...form.querySelectorAll(OPTION_GROUP)].map((group) => {
      if (group === changedGroup) return changedValue;
      return group.querySelector(`${OPTION_ITEM}.is--selected`)?.dataset.value || null;
    });
    if (selections.some((value) => !value)) return;

    const variant = productVariants(form).find((candidate) =>
      candidate.options?.every((value, index) => value === selections[index])
    );
    const select = form.querySelector('select[name="id"]');
    if (!variant || !select) return;

    select.value = String(variant.id);
    select.dispatchEvent(new Event('change', { bubbles: true }));
    syncResolvedPurchaseSurfaces(form, changedValue, variant);
    scheduleQuickShopVariantImageSync(form, variant);
    [120, 420, 900, 1500].forEach((delay) => {
      window.setTimeout(() => {
        if (confirmedSizeValue(form, changedGroup) === changedValue) {
          syncResolvedPurchaseSurfaces(form, changedValue, variant);
        }
      }, delay);
    });
    document.dispatchEvent(new CustomEvent('evano:explicit-variant-resolved', {
      detail: { form, variantId: String(variant.id) }
    }));
  };

  const forgetConfirmedSize = (form, group) => {
    confirmedSizesByForm.get(form)?.delete(groupKey(group));
    delete group.dataset.evanoConfirmedValue;
  };

  // A pending size must not reset the price to an unrelated color or finish.
  const quickShopPriceSummary = (variants, selections) => {
    const matching = variants.filter((variant) =>
      Array.isArray(variant.options)
      && selections.every((value, index) => value == null || variant.options[index] === value)
      && typeof variant.price === 'number'
      && Number.isFinite(variant.price)
      && variant.price >= 0
    );
    const available = matching.filter((variant) => variant.available);
    const candidates = available.length ? available : matching;
    if (!candidates.length) return null;

    const price = Math.min(...candidates.map((variant) => variant.price));
    const from = candidates.some((variant) => variant.price !== price);
    const compareAt = Number(candidates[0].compare_at_price || 0);
    return {
      price,
      from,
      compareAt: !from && compareAt > price && candidates.every((variant) =>
        Number(variant.compare_at_price || 0) === compareAt
      ) ? compareAt : 0
    };
  };

  const syncUnconfirmedQuickShopPrice = (form) => {
    if (!form.matches(QUICK_SHOP_FORM)) return;
    const groups = [...form.querySelectorAll(OPTION_GROUP)];
    const selections = groups.map((group) => isSizeGroup(group) ? null : selectedSizeValue(group));
    const summary = quickShopPriceSummary(productVariants(form), selections);
    const price = form.closest('.t4s-product-quick-shop')?.querySelector('[data-product-price]');
    if (!summary || !price) return;

    const money = (value) => `$${new Intl.NumberFormat('es-CO', {
      maximumFractionDigits: 0
    }).format(value / 100)} COP`;
    const current = `${summary.from ? 'Desde ' : ''}${money(summary.price)}`;
    const text = summary.compareAt ? `${money(summary.compareAt)} ${current}` : current;
    if (price.textContent.trim().replace(/\s+/g, ' ') === text) return;

    if (summary.compareAt) {
      const previous = document.createElement('del');
      previous.textContent = money(summary.compareAt);
      const discounted = document.createElement('ins');
      discounted.textContent = current;
      price.replaceChildren(previous, document.createTextNode(' '), discounted);
    } else {
      price.textContent = current;
    }
  };

  const clearUnconfirmedVariant = (form) => {
    if (!requiresExplicitSize(form)) return;

    syncUnconfirmedQuickShopPrice(form);
    const variantSelect = form.querySelector('select[name="id"]');
    if (variantSelect?.value) variantSelect.value = '';

    const mainAdd = form.querySelector('[data-atc-form], .t4s-product-form__submit');
    const mainLabel = mainAdd?.querySelector('.t4s-btn-atc_text') || mainAdd;
    if (mainLabel) mainLabel.textContent = SIZE_PROMPT;
    if (mainAdd) {
      mainAdd.disabled = false;
      mainAdd.classList.remove('t4s-d-none');
      mainAdd.removeAttribute('aria-disabled');
      mainAdd.setAttribute('aria-label', SIZE_PROMPT);
      mainAdd.dataset.evanoPurchaseState = 'unconfirmed';
    }

    const notify = notifyButton(form);
    if (notify) {
      notify.classList.add('t4s-d-none');
      notify.dataset.variantId = '';
      notify.dataset.storageid = 'notify-stock';
      notify.dataset.mfpSrc = '';
    }

    const stickyAdd = document.querySelector(`${STICKY_ATC} [data-action-atc]`);
    if (stickyAdd) stickyAdd.dataset.variantId = '';

    if (explicitSizeUrlClearedForms.has(form)) return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('variant')) {
      url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    explicitSizeUrlClearedForms.add(form);
  };

  const resetSizeGroup = (form, group) => {
    group.classList.add(UNCONFIRMED_CLASS);
    group.querySelectorAll(OPTION_ITEM).forEach((option) => {
      option.classList.remove('is--selected');
      option.setAttribute('aria-pressed', 'false');
      if (option.getAttribute('role') === 'radio') {
        option.setAttribute('aria-checked', 'false');
      }
    });

    group.querySelectorAll('[data-current-value]').forEach((currentValue) => {
      if (currentValue.textContent !== SIZE_PROMPT) {
        currentValue.textContent = SIZE_PROMPT;
      }
    });
    clearUnconfirmedVariant(form);
  };

  const hasExplicitVariantInUrl = (form) => {
    if (!initialVariantId || form.matches(QUICK_SHOP_FORM) || requiresExplicitSize(form)) return false;

    const variantOption = Array.from(
      form.querySelectorAll('select[name="id"] option')
    ).find((option) => option.value === initialVariantId);

    return Boolean(variantOption?.selected && !variantOption.disabled);
  };

  const syncQuickShopDialogName = (form) => {
    if (!form.matches(QUICK_SHOP_FORM)) return;
    const dialog = form.closest('.t4s-modal');
    const title = dialog?.querySelector('.t4s-product-qs__title');
    if (!title?.textContent.trim()) return;

    if (!title.id) title.id = `${form.getAttribute('id') || 'evano-quickshop'}-title`;
    if (dialog.getAttribute('aria-labelledby') !== title.id) {
      dialog.setAttribute('aria-labelledby', title.id);
    }
    if (dialog.getAttribute('aria-modal') !== 'true') {
      dialog.setAttribute('aria-modal', 'true');
    }
  };

  const reconcileSizePresentation = (form) => {
    syncQuickShopDialogName(form);
    const initializeExplicitVariant =
      hasExplicitVariantInUrl(form) && !explicitVariantInitializedForms.has(form);

    sizeGroups(form).forEach((group) => {
      const selectedValue = selectedSizeValue(group);
      let confirmedValue = confirmedSizeValue(form, group);

      if (!confirmedValue && initializeExplicitVariant && selectedValue) {
        rememberConfirmedSize(form, group, selectedValue);
        confirmedValue = selectedValue;
      }

      if (confirmedValue && selectedValue === confirmedValue) {
        group.dataset.evanoConfirmedValue = confirmedValue;
        group.classList.remove(UNCONFIRMED_CLASS);
        group.querySelectorAll('[data-current-value]').forEach((currentValue) => {
          if (currentValue.textContent !== confirmedValue) {
            currentValue.textContent = confirmedValue;
          }
        });
        return;
      }

      forgetConfirmedSize(form, group);
      resetSizeGroup(form, group);
    });

    if (initializeExplicitVariant) explicitVariantInitializedForms.add(form);
    syncSizeOptionAccessibility(form);
  };

  const scheduleReconcile = (form) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => reconcileSizePresentation(form));
    });
  };

  const scheduleConfirmEffectiveSizes = (form) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        sizeGroups(form).forEach((group) => {
          rememberConfirmedSize(form, group, selectedSizeValue(group));
        });
        reconcileSizePresentation(form);
      });
    });
  };

  const clearError = (...containers) => {
    containers.filter(Boolean).forEach((container) => {
      container.querySelectorAll(`.${ERROR_CLASS}`).forEach((error) => error.remove());
    });
  };

  const focusMissingSize = (group) => {
    const dropdown = group.querySelector('[data-dropdown-open]');
    const option = group.querySelector(AVAILABLE_OPTION_ITEM);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    group.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center'
    });
    window.requestAnimationFrame(() => {
      if (dropdown) {
        dropdown.focus({ preventScroll: true });
        dropdown.click();
        return;
      }

      option?.focus({ preventScroll: true });
    });
  };

  const focusStickyVariant = (sticky) => {
    const dropdown = sticky.querySelector('[data-dropdown-open]');

    if (!dropdown || (!dropdown.offsetWidth && !dropdown.offsetHeight)) return false;

    window.setTimeout(() => {
      const menuId = dropdown.getAttribute('data-id');
      const menu = menuId ? document.getElementById(menuId) : null;

      dropdown.focus({ preventScroll: true });
      if (!menu?.classList.contains('is--opened')) dropdown.click();
    }, 80);
    return true;
  };

  const hasVisibleStickyVariant = (sticky) => {
    const dropdown = sticky?.querySelector('[data-dropdown-open]');
    return Boolean(dropdown && (dropdown.offsetWidth || dropdown.offsetHeight));
  };

  const createSizeError = () => {
    const message = document.createElement('p');
    message.className = ERROR_CLASS;
    message.setAttribute('role', 'alert');
    message.setAttribute('aria-live', 'polite');
    message.textContent = 'Selecciona tu talla antes de añadir al carrito.';
    return message;
  };

  const promptInsideSticky = (sticky) => {
    clearError(sticky);

    const productPanel = sticky.querySelector('.t4s-sticky-atc__product');
    const buttonRow = sticky.querySelector('.t4s-sticky-atc__btns');
    if (!productPanel || !buttonRow || !focusStickyVariant(sticky)) return false;

    buttonRow.before(createSizeError());
    return true;
  };

  const scheduleNativeStickyPrompt = (sticky) => {
    if (promptInsideSticky(sticky)) return;

    const observer = new MutationObserver(() => {
      if (!promptInsideSticky(sticky)) return;
      observer.disconnect();
      window.clearTimeout(timeoutId);
    });
    const timeoutId = window.setTimeout(() => observer.disconnect(), 1500);

    observer.observe(sticky, {
      attributes: true,
      childList: true,
      subtree: true
    });
  };

  const requireExplicitOptions = (event, form, sticky = null) => {
    const missingGroup = sizeGroups(form).find((group) => {
      const confirmedValue = confirmedSizeValue(form, group);
      return !confirmedValue || confirmedValue !== selectedSizeValue(group);
    });

    if (!missingGroup) return false;

    pushDiagnostic('evano_add_to_cart_blocked', {
      purchase_context: sticky ? 'sticky_bar' : purchaseContext(form),
      reason: 'missing_size',
      variant_id: selectedVariantId(form)
    });

    event.preventDefault();
    event.stopImmediatePropagation();
    clearError(form, sticky);

    const message = createSizeError();

    if (sticky) {
      sticky.querySelector('.t4s-sticky-atc__btns')?.before(message);
      focusStickyVariant(sticky);
    } else {
      missingGroup.after(message);
      focusMissingSize(missingGroup);
    }

    return true;
  };

  const guardAdd = (event, form, sticky = null) => {
    if (!form) {
      event.preventDefault();
      event.stopImmediatePropagation();
      pushDiagnostic('evano_purchase_path_error', {
        reason: 'product_form_not_found',
        purchase_context: sticky ? 'sticky_bar' : 'unknown'
      });
      console.error('EVANO size guard could not resolve the product form.');
      return true;
    }

    try {
      return requireExplicitOptions(event, form, sticky);
    } catch (error) {
      event.preventDefault();
      event.stopImmediatePropagation();
      pushDiagnostic('evano_purchase_path_error', {
        reason: 'variant_guard_exception',
        purchase_context: sticky ? 'sticky_bar' : purchaseContext(form)
      });
      console.error('EVANO size guard prevented an unsafe add-to-cart.', error);
      return true;
    }
  };

  const mainProductForm = () =>
    document.querySelector(`${MAIN_PRODUCT_FORM}.is--main-sticky`);

  const quickShopTouchPoint = (event) => event.changedTouches?.[0] || event;

  const rememberQuickShopTouchStart = (event) => {
    if (event.pointerType && event.pointerType !== 'touch' && event.pointerType !== 'pen') {
      return;
    }

    const trigger = event.target.closest?.(QUICK_SHOP_TRIGGER);
    if (!trigger) return;

    const point = quickShopTouchPoint(event);
    quickShopTouchStarts.set(trigger, {
      x: point.clientX,
      y: point.clientY
    });
  };

  const recoverConsumedQuickShopTouch = (event) => {
    if (event.pointerType && event.pointerType !== 'touch' && event.pointerType !== 'pen') {
      return;
    }

    const trigger = event.target.closest?.(QUICK_SHOP_TRIGGER);
    if (!trigger || quickShopIsOpening()) return;

    const start = quickShopTouchStarts.get(trigger);
    quickShopTouchStarts.delete(trigger);
    if (!start) return;

    const point = quickShopTouchPoint(event);
    if (Math.hypot(point.clientX - start.x, point.clientY - start.y) > 12) return;

    const activation = {};
    pendingQuickShopTouchActivations.set(trigger, activation);
    window.setTimeout(() => {
      if (pendingQuickShopTouchActivations.get(trigger) !== activation) return;
      pendingQuickShopTouchActivations.delete(trigger);
      if (quickShopIsOpening()) return;

      const recovery = {};
      recoveredQuickShopActivations.set(trigger, recovery);
      window.setTimeout(() => {
        if (recoveredQuickShopActivations.get(trigger) === recovery) {
          recoveredQuickShopActivations.delete(trigger);
        }
      }, 800);
      trigger.dispatchEvent(new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      }));
    }, 120);
  };

  const quickShopTouchEvent = 'PointerEvent' in window
    ? { start: 'pointerdown', end: 'pointerup' }
    : { start: 'touchstart', end: 'touchend' };
  window.addEventListener(quickShopTouchEvent.start, rememberQuickShopTouchStart, true);
  window.addEventListener(quickShopTouchEvent.end, recoverConsumedQuickShopTouch, true);

  window.addEventListener(
    'click',
    (event) => {
      const quickShopTrigger = event.target.closest?.(QUICK_SHOP_TRIGGER);
      if (quickShopTrigger) {
        if (event.isTrusted
          && quickShopLoads.has(quickShopTrigger)
          && recoveredQuickShopActivations.has(quickShopTrigger)) {
          // A delayed native click may follow the recovered synthetic click on
          // older iOS versions. Suppress only that duplicate, not a genuine
          // second tap after an otherwise missed activation.
          recoveredQuickShopActivations.delete(quickShopTrigger);
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        pendingQuickShopTouchActivations.delete(quickShopTrigger);
        beginQuickShopLoad(event, quickShopTrigger);
        return;
      }

      const stickyAdd = event.target.closest?.(`${STICKY_ATC} [data-action-atc]`);
      if (stickyAdd) {
        if (document.body.classList.contains('evano-pdp--editorial-focus')) {
          const request = new CustomEvent('evano:sticky-add-request', {
            cancelable: true,
            detail: { originalEvent: event }
          });
          if (!document.dispatchEvent(request)) return;
        }

        const sticky = stickyAdd.closest(STICKY_ATC);
        const form = mainProductForm();
        const missingSize = form && sizeGroups(form).some((group) => {
          const confirmedValue = confirmedSizeValue(form, group);
          return !confirmedValue || confirmedValue !== selectedSizeValue(group);
        });
        const shouldUseNativePrompt =
          missingSize &&
          window.matchMedia('(max-width: 767px)').matches &&
          stickyAdd.hasAttribute('data-action-delay') &&
          !hasVisibleStickyVariant(sticky);

        if (shouldUseNativePrompt) {
          clearError(form, sticky);
          scheduleNativeStickyPrompt(sticky);
          return;
        }

        if (!guardAdd(event, form, sticky)) beginAddToCartTiming(form);
        return;
      }

      const form = event.target.closest?.(PROTECTED_FORM);
      if (form) {
        const addButton = event.target.closest?.(
          '[data-atc-form], .t4s-product-form__submit'
        );
        if (addButton && form.contains(addButton)) {
          if (!guardAdd(event, form)) beginAddToCartTiming(form);
          return;
        }

        const option = event.target.closest?.(OPTION_ITEM);
        if (option && form.contains(option)) {
          const group = option.closest(OPTION_GROUP);
          if (isSizeGroup(group)) {
            rememberConfirmedSize(form, group, option.dataset.value);
            if (requiresExplicitSize(form)) {
              // Let the theme's delegated swatch handler observe the original
              // unselected state first. Presenting the selection during capture
              // made its handler treat a real tap as a no-op, leaving the theme's
              // internal variant state incomplete even though our select had the
              // correct variant id.
              window.setTimeout(() => {
                presentConfirmedSize(group, option.dataset.value);
                resolveExplicitVariant(form, group, option.dataset.value);
                scheduleReconcile(form);
              }, 0);
            }
            clearError(form, document.querySelector(STICKY_ATC));
          } else if (form.matches(QUICK_SHOP_FORM) && requiresExplicitSize(form)) {
            window.setTimeout(() => {
              syncExplicitQuickShopChoice(form, group, option.dataset.value);
            }, 0);
          }
          scheduleReconcile(form);
          return;
        }
      }

      const stickyVariant = event.target.closest?.(
        `${STICKY_ATC} [data-dropdown-item]`
      );
      if (stickyVariant) {
        const mainForm = mainProductForm();
        if (!mainForm) return;
        clearError(mainForm, stickyVariant.closest(STICKY_ATC));
        scheduleConfirmEffectiveSizes(mainForm);
      }
    },
    true
  );

  window.addEventListener('change', (event) => {
    const option = event.target.closest?.(OPTION_ITEM);
    const group = option?.closest?.(OPTION_GROUP);
    const form = group?.closest?.(QUICK_SHOP_FORM);
    if (!form || !isSizeGroup(group) || !option.dataset.value) return;

    pushQuickShopCroEvent('size_selected', form, {
      size: option.dataset.value
    });
  }, true);

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest?.(PROTECTED_FORM);
      if (form && !guardAdd(event, form)) beginAddToCartTiming(form);
    },
    true
  );

  document.addEventListener('ajaxProduct:added', completeAddToCartTiming);

  const initializeForms = (root = document, defer = false) => {
    const forms = new Set(root.querySelectorAll?.(PROTECTED_FORM) || []);
    const owningForm = root.closest?.(PROTECTED_FORM);
    if (owningForm) forms.add(owningForm);

    forms.forEach((form) => {
      observeSizeOptionChanges(form);
      if (defer) scheduleReconcile(form);
      else reconcileSizePresentation(form);

      if (form.matches(QUICK_SHOP_FORM) && requiresExplicitSize(form)) {
        hydrateExplicitQuickShopImageSwatches(form);
        updateQuickShopSizeAvailability(form);
        scheduleQuickShopVariantContext(form);
        [120, 420, 900, 1500].forEach((delay) => {
          window.setTimeout(() => {
            hydrateExplicitQuickShopImageSwatches(form);
            updateQuickShopSizeAvailability(form);
            reconcileSizePresentation(form);
          }, delay);
        });
      }
    });
  };

  const reconcileMutatedSizeLabel = (mutation) => {
    const target = mutation.target.nodeType === Node.TEXT_NODE
      ? mutation.target.parentElement
      : mutation.target;
    const currentValue = target?.closest?.('[data-current-value]');
    const group = currentValue?.closest?.(OPTION_GROUP);
    const form = group?.closest?.(PROTECTED_FORM);
    if (!form || !isSizeGroup(group)) return;

    const selectedValue = selectedSizeValue(group);
    const confirmedValue = confirmedSizeValue(form, group);
    const expectedValue = confirmedValue && selectedValue === confirmedValue
      ? confirmedValue
      : SIZE_PROMPT;

    if (currentValue.textContent !== expectedValue) scheduleReconcile(form);
  };

  const queuedFormRoots = new Set();
  const queuedLabelForms = new Set();
  let formMutationFrame = 0;

  const scheduleFormMutationFlush = () => {
    if (formMutationFrame) return;
    formMutationFrame = window.requestAnimationFrame(() => {
      formMutationFrame = 0;
      queuedFormRoots.forEach((root) => initializeForms(root, true));
      queuedLabelForms.forEach((form) => scheduleReconcile(form));
      queuedFormRoots.clear();
      queuedLabelForms.clear();
    });
  };

  const queueMutatedSizeLabel = (mutation) => {
    const target = mutation.target.nodeType === Node.TEXT_NODE
      ? mutation.target.parentElement
      : mutation.target;
    const group = target?.closest?.('[data-current-value]')?.closest(OPTION_GROUP);
    const form = group?.closest?.(PROTECTED_FORM);
    if (form && isSizeGroup(group)) queuedLabelForms.add(form);
  };

  const observeSizeOptionChanges = (form) => {
    if (observedForms.has(form)) return;

    new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.target.closest?.(OPTION_ITEM))) {
        scheduleReconcile(form);
      }
    }).observe(form, {
      attributes: true,
      attributeFilter: ['class', 'disabled'],
      subtree: true
    });
    observedForms.add(form);
  };

  initializeForms();
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      queueMutatedSizeLabel(mutation);
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches?.(PROTECTED_FORM) || node.querySelector?.(PROTECTED_FORM)) {
          queuedFormRoots.add(node);
        }
      });
    });
    if (queuedFormRoots.size || queuedLabelForms.size) scheduleFormMutationFlush();
  }).observe(document.body, {
    characterData: true,
    childList: true,
    subtree: true
  });
})();

/* EVANO restock keyboard focus v1 — 2026-09-08. */
(() => {
  'use strict';
  const marker = 'data-evano-restock-focus';
  if (document.documentElement.hasAttribute(marker)) return;
  document.documentElement.setAttribute(marker, '');
  const triggerSelector = '[data-evano-notify-stock], [data-notify-stock-btn]';
  const modalSelector = '#remind-modal-component > div:has(form), #t4s-pr-popup__notify-stock';
  let opener = null;
  let active = null;
  let observer = null;
  let frame = 0;
  let timeout = 0;
  let redirecting = false;
  let attributes = [];

  const visible = element => {
    if (!element?.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  };
  const controls = scope => [...scope.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
    .filter(element => !element.matches(':disabled, [type="hidden"]') && element.tabIndex >= 0 && visible(element));
  const rememberAttribute = (element, name, value) => {
    const previous = element.getAttribute(name);
    if (previous === value) return;
    attributes.push({ element, name, previous, value });
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  };
  const focus = element => {
    if (!visible(element)) return;
    redirecting = true;
    element.focus({ preventScroll: true });
    redirecting = false;
  };
  const initialTarget = () => active?.querySelector('h2, h3') || controls(active)[0] || active;
  const restoreAttributes = () => {
    for (const { element, name, previous, value } of attributes) {
      if (element.getAttribute(name) !== value) continue;
      if (previous === null) element.removeAttribute(name);
      else element.setAttribute(name, previous);
    }
    attributes = [];
  };
  const stop = restoreFocus => {
    observer?.disconnect();
    observer = null;
    cancelAnimationFrame(frame);
    clearTimeout(timeout);
    frame = 0;
    active = null;
    restoreAttributes();
    if (restoreFocus && visible(opener)) focus(opener);
    opener = null;
  };
  const sync = () => {
    frame = 0;
    const next = [...document.querySelectorAll(modalSelector)].find(visible);
    if (!next) {
      if (active) stop(true);
      return;
    }
    if (next === active) return;
    restoreAttributes();
    active = next;
    clearTimeout(timeout);
    // Include the theme close button, which sits outside its inner dialog.
    active.querySelectorAll('[role="dialog"]').forEach(inner => {
      rememberAttribute(inner, 'role', null);
      rememberAttribute(inner, 'aria-modal', null);
    });
    rememberAttribute(active, 'role', 'dialog');
    rememberAttribute(active, 'aria-modal', 'true');
    const close = active.querySelector('.evano-remind-modal-close, .mfp-close');
    if (close && !close.hasAttribute('aria-label')) {
      rememberAttribute(close, 'aria-label', 'Cerrar aviso de disponibilidad');
    }
    if (!active.hasAttribute('aria-labelledby') && !active.hasAttribute('aria-label')) {
      rememberAttribute(active, 'aria-label', 'Avísame cuando vuelva');
    }
    const target = initialTarget();
    if (!target.hasAttribute('tabindex')) rememberAttribute(target, 'tabindex', '-1');
    focus(target);
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(sync);
  };
  const begin = trigger => {
    if (observer) return;
    opener = trigger;
    observer = new MutationObserver(mutations => {
      if (!active || mutations.some(mutation => mutation.type === 'childList' ||
          active.contains(mutation.target) || mutation.target.contains(active))) schedule();
    });
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
    });
    timeout = setTimeout(() => { if (!active) stop(false); }, 15000);
    schedule();
  };
  // Window capture runs before the existing application bridge consumes click.
  window.addEventListener('click', event => {
    const trigger = event.target.closest?.(triggerSelector);
    if (trigger && visible(trigger)) begin(trigger);
    else if (active) schedule();
  }, true);
  window.addEventListener('focusin', event => {
    if (!active || redirecting || !visible(active) || active.contains(event.target)) return;
    focus(initialTarget());
  }, true);
  window.addEventListener('keydown', event => {
    if (!active || !visible(active)) return;
    if (event.key === 'Tab') {
      const items = controls(active);
      const index = items.indexOf(document.activeElement);
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!items.length) focus(initialTarget());
      else {
        const next = index < 0 ? (event.shiftKey ? items.length - 1 : 0)
          : (index + (event.shiftKey ? -1 : 1) + items.length) % items.length;
        focus(items[next]);
      }
    } else if (event.key === 'Escape') {
      const close = active.querySelector('.evano-remind-modal-close, .mfp-close, [aria-label="Cerrar aviso de disponibilidad"]');
      if (!visible(close)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      close.click();
      schedule();
    }
  }, true);
  window.addEventListener('pagehide', () => stop(false));
})();
/* End EVANO restock keyboard focus v1. */
